import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import TextButton from '@oberoninternal/travelbase-ds/components/action/TextButton';
import Img, { BaseImgProps } from '../Img';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { Flex, FlexProps } from '@rebass/grid';
import Link, { LinkProps } from 'next/link';
import React, { ComponentProps, forwardRef, Ref } from 'react';
import styled, { css } from 'styled-components';
import AsyncButton from '../AsyncButton';

export interface OrderItemProps extends Omit<FlexProps, 'data' | 'ref'> {
    linkProps?: LinkProps;
    renderAccommodationLink?: boolean;
}

const OrderItem = (
    { children, linkProps, renderAccommodationLink, ...props }: OrderItemProps,
    ref: Ref<HTMLDivElement>
) => (
    <OrderItemContainer
        ref={ref}
        flexDirection={['column', null, null, 'row']}
        justifyContent={['space-between']}
        {...props}
    >
        {linkProps && !renderAccommodationLink && (
            <Link passHref {...linkProps} legacyBehavior>
                <LinkOverlay />
            </Link>
        )}
        {children}
    </OrderItemContainer>
);

export default forwardRef(OrderItem);

const LinkOverlay = styled.a`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
`;

const OrderItemContainer = styled(Flex)`
    position: relative;
    margin-right: 1.2rem;
    padding: 0;
    background: rgb(255, 255, 255);
    border-radius: 0.8rem;

    transition: all 0.15s ease-in-out;
    :hover {
        box-shadow: rgba(59, 118, 160, 0.2) 0px 16px 30px 0px, rgba(59, 118, 160, 0.2) 0px 24px 80px 0px;
    }
    box-shadow: rgba(59, 118, 160, 0.1) 0px 16px 24px 0px, rgba(59, 118, 160, 0.1) 0px 24px 80px 0px;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        border: 1px solid rgb(247, 248, 249);
        width: 100%;
        padding-right: ${({ theme }) => theme.spacing['70_XLarge']};
        margin-bottom: 2.9rem;

        :last-of-type {
            margin-bottom: 0;
        }

        margin-right: 0;
        align-items: center;
    }
`;

export const OrderItemThumbnail = ({ faded = false, ...props }: BaseImgProps & { faded?: boolean }) => (
    <ThumbnailContainer faded={faded}>
        <Img {...props} layout="fill" />
    </ThumbnailContainer>
);

const ThumbnailContainer = styled.div<{ faded: boolean }>`
    opacity: ${({ faded }) => (faded ? 0.4 : 1)};
    position: relative;
    width: 100%;
    height: 19.1rem;
    overflow: hidden;
    border-top-left-radius: 0.8rem;
    border-top-right-radius: 0.8rem;
    flex-shrink: 0;

    img {
        object-fit: cover;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xs}) and (max-width: ${({ theme }) =>
            theme.mediaQueries.xl}) {
        display: none;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
        border-bottom-left-radius: 0.8rem;
        border-top-right-radius: 0;
        margin-right: ${({ theme }) => theme.spacing['50_Semi']};
        width: 20.8rem;
        flex-shrink: 0;
    }
`;

export const OrderItemInfo = styled(Flex).attrs({ flexDirection: 'column' })`
    padding: ${({ theme }) => theme.spacing['40_Standard']};
    width: 100%;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        padding: 0;
        padding-right: 1.6rem;
        margin-top: 0;
        width: unset;
    }
`;

export const OrderItemDisclaimer = styled(Body).attrs({ variant: 'small' })`
    font-weight: 500;
    margin-bottom: ${({ theme }) => theme.spacing['20_Tiny']};
    color: ${({ theme }) => theme.colors.primary['50']};
`;

export const OrderItemName = styled(Body).attrs({ variant: 'huge' })`
    color: ${({ theme }) => theme.colors.neutral['70']};
    font-weight: 600;
    margin-bottom: 1.2rem;
`;

export const OrderItemTextButton = (props: ComponentProps<typeof TextButton>) => (
    <StyledTextButton
        {...props}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            props.onClick?.(e);
        }}
    />
);

export const OrderItemButton = ({ onClick, ...props }: ComponentProps<typeof Button>) => (
    <StyledButton
        {...props}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            onClick?.(e);
        }}
    />
);

const buttonStyles = css`
    justify-self: flex-end;
    margin: ${({ theme }) => theme.spacing['50_Semi']} 0;
    margin-left: ${({ theme }) => theme.spacing['40_Standard']};
    align-self: flex-start;
    z-index: 1;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        align-self: unset;
        margin: 0;
    }
`;

const StyledButton = styled(AsyncButton)`
    ${buttonStyles};
`;

const StyledTextButton = styled(TextButton)`
    ${buttonStyles}
`;

export const OrderItems = styled(Flex)`
    margin-bottom: ${({ theme }) => theme.spacing['40_Standard']};

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        margin: 0;
        padding-bottom: ${({ theme }) => theme.spacing['60_Large']};
        flex-direction: row;
        overflow-x: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
        /* make it smooth on iOS */
        -webkit-overflow-scrolling: touch;
        &::-webkit-scrollbar {
            display: none;
        }
        width: calc(100% + var(--wrapperPadding) * 2);
        margin-left: calc(var(--wrapperPadding) * -1);

        > div:first-of-type {
            margin-left: var(--wrapperPadding);
        }
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        > * + * {
            margin-top: 1.6rem;
        }
    }
    flex-direction: column;
`;
